import { ForwardRefRenderFunction, forwardRef, useCallback, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useAtomValue } from "jotai"
import { ClusterThumbnail, MemoizedThumbnail } from "@/components/ui/Thumbnail"
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual"
import { useVisibleThumbnails } from "@/utils/useVisibleThumbnails"
import { resetAllFilters, hasFiltersAtom } from "@/store/photo/photos-view-state"
import { Button } from "@/components/ui/Button"
import {
	currentPhotoAtom,
	displayedPhotoIdsAtom,
	displayedPhotosAtom,
} from "@/store/photo/photos-state"
import { useScrollToCurrentPhoto } from "./useScrollToCurrentPhoto"
import { useAutoCorrectSelection } from "./useAutoCorrectSelection"
import { ScrollBar } from "@/components/ui/ScrollBar"
import { detectTrackPad } from "@/utils/is-trackpad"
import { Range } from "@tanstack/virtual-core"
import { isCullingModeAtom, projectAtom } from "@/store/project/projects-state"
import { isImportingAtom } from "@/store/app-state"
import {
	clusteredCoverPhotoIdsAtom,
	clusteredCoverPhotosAtom,
	currentClusterIdAtom,
	currentClusterPhotosAtom,
	groupSimilarPhotosAtom,
	isShowingClustersAtom,
	photoClustersListAtom,
} from "@/store/importing/culling-state"
import clsx from "clsx"
import React from "react"
import { FilmstripPhotos } from "./FilmstripPhotos"
import { useCenterNewlyMarkedReferences } from "./useCenterNewlyMarkedReferences"
import { showingFilmstripAtom } from "@/store/ui-views-state"
import { CLUSTER_THUMBNAIL_WIDTH_PX, PHOTO_THUMBNAIL_WIDTH_PX } from "./filmstrip.config"
import { clearEditingControlFocus } from "@/store/editor/editing-control-focus"

const FilmstripInternal: ForwardRefRenderFunction<HTMLDivElement> = (_, ref) => {
	const { t } = useTranslation("components")
	const parentRef = useRef<HTMLDivElement | null>(null)
	const stackedParentRef = useRef<HTMLDivElement | null>(null)
	const isShown = useAtomValue(showingFilmstripAtom)

	const hasFilters = useAtomValue(hasFiltersAtom)

	const displayedPhotos = useAtomValue(displayedPhotosAtom)
	const displayedPhotoIds = useAtomValue(displayedPhotoIdsAtom)
	const coverPhotos = useAtomValue(clusteredCoverPhotosAtom)
	const coverPhotoIds = useAtomValue(clusteredCoverPhotoIdsAtom)
	const currentClusterId = useAtomValue(currentClusterIdAtom)
	const currentPhotoCluster = useAtomValue(currentClusterPhotosAtom)
	const clusters = useAtomValue(photoClustersListAtom)

	const currentPhoto = useAtomValue(currentPhotoAtom)
	const project = useAtomValue(projectAtom)

	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isImporting = useAtomValue(isImportingAtom)
	const isShowingClusters = useAtomValue(isShowingClustersAtom)
	const groupSimilarPhotos = useAtomValue(groupSimilarPhotosAtom)

	// Create a stable reference based on photo IDs to prevent unnecessary re-renders during processing
	const currentPhotoClusterIds = useMemo(
		() => currentPhotoCluster.map((photo) => photo.id),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentPhotoCluster.map((photo) => photo.id).join(",")],
	)

	// Clusters
	const visibleClusterRangeRef = useRef<[number, number]>([0, 0])
	const clustersVirtualizer = useVirtualizer({
		horizontal: true,
		count: coverPhotoIds.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => CLUSTER_THUMBNAIL_WIDTH_PX,
		overscan: 5,
		rangeExtractor: useCallback((range: Range) => {
			visibleClusterRangeRef.current = [range.startIndex, range.endIndex]
			return defaultRangeExtractor(range)
		}, []),
	})
	const clusterCols = clustersVirtualizer.getVirtualItems()
	const visibleClusterCols = clusterCols.filter(
		(col) =>
			col.index >= (clustersVirtualizer.range?.startIndex ?? 0) &&
			col.index <= (clustersVirtualizer.range?.endIndex ?? 0),
	)

	// Photos
	const visiblePhotosRangeRef = useRef<[number, number]>([0, 0])
	const photosVirtualizer = useVirtualizer({
		horizontal: true,
		count: displayedPhotos.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => PHOTO_THUMBNAIL_WIDTH_PX,
		overscan: 5,
		rangeExtractor: useCallback((range: Range) => {
			visiblePhotosRangeRef.current = [range.startIndex, range.endIndex]
			return defaultRangeExtractor(range)
		}, []),
	})

	useCenterNewlyMarkedReferences({
		columnVirtualizer: photosVirtualizer,
		isShown,
	})

	const photosCols = photosVirtualizer.getVirtualItems()
	const visiblePhotoCols = photosCols.filter(
		(col) =>
			col.index >= (photosVirtualizer.range?.startIndex ?? 0) &&
			col.index <= (photosVirtualizer.range?.endIndex ?? 0),
	)

	// Group members scroll independently of the covers. Include only the members
	// on screen so their final previews load without rendering the entire group.
	const stackedPhotosVirtualizer = useVirtualizer({
		horizontal: true,
		count: currentPhotoClusterIds.length,
		getScrollElement: () => stackedParentRef.current,
		estimateSize: () => PHOTO_THUMBNAIL_WIDTH_PX,
		overscan: 0,
		enabled: isShown && isShowingClusters,
	})
	const stackedPhotoCols = stackedPhotosVirtualizer.getVirtualItems()
	const visibleStackedPhotoIds = useMemo(
		() =>
			stackedPhotoCols
				.map((col) => currentPhotoClusterIds[col.index])
				.filter((id): id is string => id !== undefined),
		[stackedPhotoCols, currentPhotoClusterIds],
	)
	useVisibleThumbnails({
		cols: isShowingClusters ? visibleClusterCols : visiblePhotoCols,
		photoIds: isShowingClusters ? coverPhotoIds : displayedPhotoIds,
		additionalPhotoIds: visibleStackedPhotoIds,
		isShown,
	})

	// Handle scrolling to current photo when selection changes
	useScrollToCurrentPhoto({
		isShown,
		currentPhoto,
		isImporting,
		isShowingClusters,
		coverPhotoIds,
		displayedPhotoIds,
		clustersVirtualizer,
		photosVirtualizer,
		currentPhotoClusterIds,
		currentClusterId,
	})

	useAutoCorrectSelection({
		currentPhoto,
		currentPhotoClusterIds,
		displayedPhotoIds,
		isShowingClusters,
	})

	const handleScrollWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		const isTrackPad = detectTrackPad(e.nativeEvent)

		// Map vertical scrolling to horizontal scrolling
		// When the user tries to scroll up and down, we instead scroll left and right
		if (!e.deltaX && !isTrackPad) {
			e.currentTarget.scrollLeft += e.deltaY
		}
	}

	const renderClusters = () => {
		return (
			<div
				className={`relative m-1 mb-0 h-[72px]`}
				style={{ width: clustersVirtualizer.getTotalSize() }}
			>
				{clusterCols.map((virtualColumn) => {
					const coverPhoto = coverPhotos[virtualColumn.index]
					const cluster = clusters[virtualColumn.index]

					if (!coverPhoto || !cluster || !project) {
						return null
					}
					return (
						<div
							key={cluster.clusterLeaderId}
							className={`absolute left-0 top-0 h-full w-[72px]`}
							style={{
								transform: `translateX(${virtualColumn.start}px)`,
							}}
						>
							<ClusterThumbnail
								coverPhoto={coverPhoto}
								cluster={cluster}
								project={project}
							/>
						</div>
					)
				})}
			</div>
		)
	}

	return (
		<div
			aria-label={t("filmstrip.filmstripPhotos", { length: displayedPhotos.length })}
			className={`relative flex h-fit flex-none flex-col border-t border-base-900`}
			id="filmstrip"
			onPointerDownCapture={clearEditingControlFocus}
			ref={ref}
		>
			{isCullingMode && groupSimilarPhotos && coverPhotos.length > 0 && (
				<div
					ref={stackedParentRef}
					style={{ justifyContent: "safe center" }}
					onWheel={handleScrollWheel}
					className="no-scrollbar flex h-[116px] w-full flex-row items-center space-x-1 overflow-x-scroll bg-base-800"
					id="stacked_container"
				>
					{currentPhotoCluster.map((photo) => {
						if (!photo || !project) {
							return null
						}
						return (
							<div
								className="size-[112px] shrink-0"
								key={photo.id}
								id={`stacked_${photo.id}`}
							>
								<MemoizedThumbnail
									size={"small"}
									photo={photo}
									project={project}
									isStackedPhoto={true}
								/>
							</div>
						)
					})}
				</div>
			)}
			<div className="relative flex" data-testid="filmstrip_scrollarea">
				{displayedPhotos.length === 0 && hasFilters === true && (
					<div
						className={clsx(
							isCullingMode && groupSimilarPhotos ? "h-[90px]" : "h-[132px]",
							"flex h-[132px] w-full flex-col items-center justify-center",
						)}
					>
						<div className="m-1 text-sm text-base-400">
							{t("filmstrip.noPhotosMatching")}
						</div>
						<Button
							trackingLabel="reset_filters"
							aria-label={t("filmstrip.ariaResetFilters")}
							size="sm"
							className="mt-2"
							onClick={() => {
								resetAllFilters()
							}}
						>
							{t("filmstrip.resetFilters")}
						</Button>
					</div>
				)}

				{project && (
					<div
						ref={parentRef}
						className="no-scrollbar overflow-y-hidden pb-4"
						onWheel={handleScrollWheel}
					>
						{isCullingMode && groupSimilarPhotos ? (
							renderClusters()
						) : (
							<FilmstripPhotos
								project={project}
								photosVirtualizer={photosVirtualizer}
								displayedPhotos={displayedPhotos}
							/>
						)}
						<ScrollBar horizontal />
					</div>
				)}
			</div>
		</div>
	)
}

export const Filmstrip = forwardRef<HTMLDivElement>(FilmstripInternal)
